import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../assets/image.webp";
import { Eye, EyeOff, AtSign, HelpCircle, Mail, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MyContext from "../ContextApi/MyContext";
import DisclaimerModal from "./DisclaimerModal";

// Must stay >= the backend's OTP_VALIDITY_SECONDS (auth_api/views.py).
const OTP_VALIDITY_MS = 15 * 60 * 1000;
const RESEND_COOLDOWN_SECONDS = 30;

const ProviderLogin = () => {
  const { api, fetchProviders } = useContext(MyContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    ip_address: "",
    browser_info: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const [step, setStep] = useState("login");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState(
    sessionStorage.getItem("verificationToken") || ""
  );
  const [resendIn, setResendIn] = useState(0);

  // Fetch IP Address
  const fetchIpAddress = async () => {
    try {
      const res = await axios.get("https://api64.ipify.org?format=json");
      setFormData((prev) => ({ ...prev, ip_address: res.data.ip }));
    } catch (err) {
      console.error("IP fetch error:", err);
    }
  };

  // Drop a half-finished OTP attempt. Anything left in sessionStorage would
  // otherwise drop the user straight onto the OTP screen with a dead token and
  // no new email on the way.
  const clearOtpSession = () => {
    sessionStorage.removeItem("verificationToken");
    sessionStorage.removeItem("otpEmail");
    sessionStorage.removeItem("otpIssuedAt");
    setVerificationToken("");
    setOtp("");
  };

  useEffect(() => {
    fetchIpAddress();

    setFormData((prev) => ({
      ...prev,
      browser_info: navigator.userAgent,
    }));

    const token = sessionStorage.getItem("verificationToken");
    const storedEmail = sessionStorage.getItem("otpEmail");
    const issuedAt = Number(sessionStorage.getItem("otpIssuedAt") || 0);
    const isFresh = issuedAt > 0 && Date.now() - issuedAt < OTP_VALIDITY_MS;

    // Only resume the OTP step while the token can still be verified.
    if (token && storedEmail && isFresh) {
      setVerificationToken(token);
      setFormData((prev) => ({
        ...prev,
        email: storedEmail,
      }));
      setStep("otp");
    } else {
      clearOtpSession();
      setStep("login");
    }
  }, []);

  // Resend cooldown tick
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // STEP 1 — Send Login OTP
  const handleLogin = async () => {
    if (!validateForm()) return;

    // Canonical form — the backend signs the OTP against the normalized
    // address, and this keeps the displayed/stored value consistent with it.
    const email = formData.email.trim().toLowerCase();

    setIsLoading(true);
    try {
      const response = await axios.post(`${api}/auth/send-login-otp/`, {
        email,
        password: formData.password,
        ip_address: formData.ip_address,
        browser_info: formData.browser_info,
      });

      if (response.data.verification_token) {
        setVerificationToken(response.data.verification_token);
        setFormData((prev) => ({ ...prev, email }));
        setOtp("");

        sessionStorage.setItem(
          "verificationToken",
          response.data.verification_token
        );

        sessionStorage.setItem("otpEmail", email);
        sessionStorage.setItem("otpIssuedAt", String(Date.now()));

        setResendIn(RESEND_COOLDOWN_SECONDS);
        setStep("otp");
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Invalid credentials. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Re-send a fresh OTP. Only the newest code is valid, so the previous token
  // is dropped before asking for a new one.
  const handleResendOtp = async () => {
    if (resendIn > 0 || isLoading) return;

    // The password is deliberately never persisted, so a page reload leaves us
    // without it — send the user back to step 1 rather than failing silently.
    if (!formData.password) {
      toast.info("Please enter your password again to get a new OTP.");
      clearOtpSession();
      setStep("login");
      return;
    }

    await handleLogin();
  };

  // STEP 2 — Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${api}/auth/verify-login-otp/`, {
        otp,
        verification_token: verificationToken,
        ip_address: formData.ip_address,
        browser_info: formData.browser_info,
      });

      if (response.data.token) {
        console.log(response)
        // Store login data but don't navigate yet — show disclaimer first
        setPendingLoginData(response.data);
        setShowDisclaimer(true);
      }
    } catch (error) {
      const message =
        error.response?.data?.error ||
        "Invalid or expired OTP. Please try again.";
      toast.error(message);

      // A mistyped code is retryable; a dead token is not — send those back to
      // step 1 instead of leaving the user stuck on an OTP that can never pass.
      if (/expired|verification token/i.test(message)) {
        clearOtpSession();
        setStep("login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisclaimerAccept = async () => {
    if (!pendingLoginData) return;

    const userData = pendingLoginData.user;
    const pns = userData.provider_numbers || [];
    const primary = pns.find((p) => p.is_primary) || pns[0] || null;
    userData.active_provider_no = primary?.provider_no || userData.provider_no || "";
    userData.active_provider_id = primary?.id || null;

    localStorage.setItem("authToken", pendingLoginData.token);
    localStorage.setItem("user", JSON.stringify(userData));

    await fetchProviders();

    toast.success("Login successful!");

    setOtp("");
    setVerificationToken("");
    sessionStorage.removeItem("verificationToken");
    setStep("login");
    setShowDisclaimer(false);
    setPendingLoginData(null);

    (userData.is_admin || userData.is_guest) ? navigate("/admin") : navigate("/verify");
  };

  const handleDisclaimerDecline = () => {
    setShowDisclaimer(false);
    setPendingLoginData(null);
    setOtp("");
    setVerificationToken("");
    sessionStorage.removeItem("verificationToken");
    sessionStorage.removeItem("otpEmail");
    setStep("login");
    setFormData((prev) => ({ ...prev, email: "", password: "" }));
    toast.info("You must accept the Terms of Use to access this website.");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const divStyle = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  return (
    <div
      style={divStyle}
      className="min-h-screen flex flex-col justify-center items-center gap-4"
    >
      <div className="flex flex-col justify-center bg-white gap-4 p-6 rounded-xl w-80 border-2">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800">
            PROVIDER LOGIN
          </h3>
        </div>

        {/* Email Field */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-600 text-sm">
            Registered Email<span className="text-red-600">*</span>
          </label>
          <div
            className={`flex flex-row bg-white border-2 rounded justify-between h-10 p-2 ${errors.email ? "border-red-500" : "border-gray-300"
              }`}
          >
            <input
              className="border-0 w-full outline-none text-sm"
              type="email"
              name="email"
              placeholder="Ex: user@example.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <div className="border-l-2 pl-2">
              <AtSign className="w-4 h-4 text-[#0486A5]" />
            </div>
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs">{errors.email}</p>
          )}
        </div>

        {/* Password Field */}
        {step === "login" && (
          <div className="flex flex-col gap-1">
            <label className="text-gray-600 text-sm">
              Password<span className="text-red-600">*</span>
            </label>
            <div
              className={`flex flex-row bg-white border-2 rounded justify-between h-10 p-2 ${errors.password ? "border-red-500" : "border-gray-300"
                }`}
            >
              <input
                className="border-0 w-full outline-none text-sm"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
              <div
                className="border-l-2 pl-2 cursor-pointer"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 text-[#0486A5]" />
                ) : (
                  <Eye className="w-4 h-4 text-[#0486A5]" />
                )}
              </div>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs">{errors.password}</p>
            )}
          </div>
        )}

        {/* OTP Step */}
        {step === "otp" && (
          <div className="flex flex-col gap-1">
            <label className="text-gray-600 text-sm">
              Enter OTP<span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              maxLength="6"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setOtp(value);
              }}
              className="border-2 rounded h-10 p-2 text-sm"
              placeholder="Enter 6 digit OTP"
            />
            <p className="text-gray-500 text-xs">
              Sent to {formData.email}. If you requested more than one code,
              enter the most recent — earlier codes stop working.
            </p>
          </div>
        )}

        {/* Button */}
        {step === "login" ? (
          <button
            className="bg-[#0486A5] hover:bg-[#047B95] py-2 px-6 text-white rounded-lg text-sm"
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading ? "Sending OTP..." : "Login"}
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              className="bg-[#0486A5] hover:bg-[#047B95] py-2 px-6 text-white rounded-lg text-sm"
              onClick={handleVerifyOtp}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              className="py-2 px-6 text-[#0486A5] border border-[#0486A5] hover:bg-gray-50 rounded-lg text-sm disabled:opacity-50"
              onClick={handleResendOtp}
              disabled={isLoading || resendIn > 0}
            >
              {resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
            </button>
            <button
              className="py-2 px-6 text-[#0486A5] border border-[#0486A5] hover:bg-gray-50 rounded-lg text-sm"
              onClick={() => {
                setStep("login");
                clearOtpSession();
                setFormData((prev) => ({ ...prev, email: "", password: "" }));
              }}
              disabled={isLoading}
            >
              Use a different email
            </button>
          </div>
        )}

        {/* Forgot Password Link */}
        {step === "login" && (
          <div className="text-center -mt-2">
            <a
              href="/forgot-password"
              className="text-sm text-[#0486A5] hover:underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                navigate("/forgot-password");
              }}
            >
              Forgot Password?
            </a>
          </div>
        )}

        {/* Register Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            No account yet?{" "}
            <a
              href="/register"
              className="text-[#0486A5] hover:underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                navigate("/register");
              }}
            >
              Register as a new provider.
            </a>
          </p>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 w-80 border-2 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <HelpCircle className="w-5 h-5 text-[#0486A5]" />
          <h4 className="text-sm font-semibold text-gray-800">Need Help?</h4>
        </div>
        <div className="flex flex-col gap-2 text-sm text-gray-600">
          <a
            href="mailto:providers@mmpplans.com"
            className="flex items-center justify-center gap-2 hover:text-[#0486A5] transition-colors"
          >
            <Mail className="w-4 h-4 text-[#0486A5]" />
            providers@mmpplans.com
          </a>
          <a
            href="tel:410-850-8638"
            className="flex items-center justify-center gap-2 hover:text-[#0486A5] transition-colors"
          >
            <Phone className="w-4 h-4 text-[#0486A5]" />
            410-850-8638
          </a>
        </div>
      </div>

      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        onDecline={handleDisclaimerDecline}
        context="login"
      />
    </div>
  );
};

export default ProviderLogin;
